import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Paper, 
  Typography, 
  List, 
  ListItem, 
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Box
} from '@mui/material';
import { NoteAlt as NoteIcon } from '@mui/icons-material';
import { fetchLatestNotes } from '../../features/notifications/notificationsSlice';
import { formatDistanceToNow } from 'date-fns';

const renderContent = (content) => {
  try {
    if (!content) return '';
    const data = typeof content === 'string' ? JSON.parse(content) : content;
    
    if (!data || !Array.isArray(data.blocks)) {
      return content || '';
    }
    
    return data.blocks.map(block => {
      if (!block || !block.type) return '';
      
      switch (block.type) {
        case 'paragraph':
          return `<p>${block.data?.text || ''}</p>`;
        case 'header':
          return `<h${block.data?.level || 2}>${block.data?.text || ''}</h${block.data?.level || 2}>`;
        case 'list':
          if (!block.data?.items) return '';
          const listItems = block.data.items
            .map(item => {
              if (typeof item === 'object' && item.content) {
                return `<li>${item.content}</li>`;
              }
              return `<li>${item || ''}</li>`;
            })
            .join('');
          
          const listStyle = block.data.style === 'ordered' ? 'decimal' : 'disc';
          return `
            <${block.data.style === 'ordered' ? 'ol' : 'ul'} style="list-style-type: ${listStyle}; margin-left: 20px;">
              ${listItems}
            </${block.data.style === 'ordered' ? 'ol' : 'ul'}>
          `;
        case 'image':
          return `<img src="${block.data?.url || ''}" alt="${block.data?.caption || ''}" style="max-width: 100%;" />`;
        default:
          return '';
      }
    }).join('');
  } catch (error) {
    console.error('Error rendering content:', error);
    return content || '';
  }
};

const Notifications = () => {
  const dispatch = useDispatch();
  const { latestNotes, loading, error } = useSelector((state) => state.notifications);
  const { isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchLatestNotes())
        .unwrap()
        .then(data => {
          console.log('Notes fetched:', data);
          if (!Array.isArray(data)) {
            console.error('Expected array of notes but got:', data);
          }
        })
        .catch(err => {
          console.error('Error fetching notes:', err);
        });
    }
  }, [dispatch, isAuthenticated]);

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // Ensure latestNotes is an array
  const notesList = Array.isArray(latestNotes) ? latestNotes : [];

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          Latest Notes
        </Typography>
        {notesList.length === 0 ? (
          <Alert severity="info">No notes found</Alert>
        ) : (
          <List>
            {notesList.map((note, index) => (
              <React.Fragment key={note?.id || index}>
                <ListItem
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    boxShadow: 1
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ mr: 2 }}>
                        {(note?.username?.[0] || 'U').toUpperCase()}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2">
                          {note?.username || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Class {note?.class_number || '?'} • {
                            note?.updated_at ? 
                              formatDistanceToNow(new Date(note.updated_at), { addSuffix: true }) :
                              'Unknown time'
                          }
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      dangerouslySetInnerHTML={{ __html: renderContent(note?.content) }}
                      sx={{
                        '& img': { maxWidth: '100%' },
                        '& p': { margin: '0.5em 0' },
                        '& h2': { fontSize: '1.3em', margin: '0.5em 0' },
                        '& h3': { fontSize: '1.1em', margin: '0.5em 0' },
                        '& ul, & ol': {
                          margin: '0.5em 0',
                          paddingLeft: '1.5em',
                          listStylePosition: 'outside'
                        },
                        '& li': {
                          margin: '0.25em 0',
                          paddingLeft: '0.5em'
                        }
                      }}
                    />
                  </Box>
                </ListItem>
                {index < notesList.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Container>
  );
};

export default Notifications; 